/**
 * 인증 상태 스토어(zustand) — FO Pinia auth 스토어 이식.
 * 인증 수준(guest/pending/verified) + 역할(roles) + 워크스페이스 유형·기능(axis·features) + 세션 이메일.
 * 부팅 시 restoreSession() 1회로 서버 세션 복원. 워크스페이스 화면 판단은 roles 가 아니라 axis·features 로 한다(specs/064).
 * 클라 가드는 UX 레이어일 뿐 — 실제 강제는 백엔드 @PreAuthorize.
 */
import { create } from 'zustand';

import { fetchSession, logout as logoutApi, type SessionInfo } from '@/api/auth';
import { LOGIN_FLOW_TTL_MS } from '@/config/auth';
import type { MemberAxis, WorkspaceFeature } from '@/config/workspace-features';
import { safeInternalPath } from '@/lib/safePath';
import { queryClient } from '@/lib/queryClient';

export type AuthLevel = 'guest' | 'pending' | 'verified';

const RETURN_URL_KEY = 'fo.auth.returnUrl';

interface AuthState {
  level: AuthLevel;
  email: string | null;
  name: string | null;
  roles: readonly string[];
  axis: MemberAxis | null;
  features: readonly WorkspaceFeature[];
  /** 부팅 세션 복원 완료 여부 — 가드가 보호 라우트 판정 전 보장. */
  sessionRestored: boolean;
  /**
   * 세션 확인이 401 이 아닌 오류(네트워크·5xx)로 실패함 — 로그인 여부를 모르는 상태라 guest 로 단정하지 않는다(살아 있는 세션을
   * 로그인 화면으로 튕기지 않게). 가드가 재시도 화면을 보인다.
   */
  sessionError: boolean;
  /** 로그인 여부(pending|verified). */
  isAuthenticated: () => boolean;
  applySession: (session: SessionInfo) => void;
  /** 표시 이름 변경 직후 반영(specs/053) — 세션 재조회 없이 카드·메뉴 갱신. */
  setName: (name: string) => void;
  restoreSession: () => Promise<void>;
  /** 실패한 세션 확인을 다시 시도한다. */
  retrySession: () => Promise<void>;
  logout: () => Promise<void>;
  setReturnUrl: (url: string) => void;
  consumeReturnUrl: () => string | null;
}

let restorePromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()((set, get) => ({
  level: 'guest',
  email: null,
  name: null,
  roles: [],
  axis: null,
  features: [],
  sessionRestored: false,
  sessionError: false,

  isAuthenticated: () => get().level !== 'guest',

  applySession: (session) => {
    if (session.authenticated) {
      set({
        level: 'verified',
        email: session.email,
        name: session.name,
        roles: session.roles,
        axis: session.axis,
        features: session.features,
        sessionRestored: true,
        sessionError: false,
      });
    } else {
      set({
        level: 'guest',
        email: null,
        name: null,
        roles: [],
        axis: null,
        features: [],
        sessionRestored: true,
        sessionError: false,
      });
    }
  },

  setName: (name) => set({ name }),

  restoreSession: async () => {
    if (get().sessionRestored) return;
    if (restorePromise) return restorePromise;
    restorePromise = (async () => {
      try {
        get().applySession(await fetchSession());
      } catch {
        // 오류 토스트는 http 인터셉터가 1회 띄운다 — 여기서는 재시도 가능한 종료 상태로만 남긴다.
        set({ sessionError: true });
      } finally {
        restorePromise = null;
      }
    })();
    return restorePromise;
  },

  retrySession: () => {
    set({ sessionError: false });
    return get().restoreSession();
  },

  /** 로컬 상태 정리의 단일 지점 — 인증 guest, 복귀지 폐기, 쿼리 캐시 비움. */
  logout: async () => {
    try {
      await logoutApi();
    } finally {
      get().consumeReturnUrl();
      queryClient.clear();
      set({
        level: 'guest',
        email: null,
        name: null,
        roles: [],
        axis: null,
        features: [],
        sessionRestored: false,
      });
    }
  },

  setReturnUrl: (url) => {
    if (!safeInternalPath(url)) return;
    try {
      sessionStorage.setItem(
        RETURN_URL_KEY,
        JSON.stringify({ v: url, exp: Date.now() + LOGIN_FLOW_TTL_MS }),
      );
    } catch {
      /* noop */
    }
  },

  consumeReturnUrl: () => {
    try {
      const raw = sessionStorage.getItem(RETURN_URL_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      if (!raw) return null;
      const { v, exp } = JSON.parse(raw) as { v: string; exp: number };
      return Date.now() < exp ? safeInternalPath(v) : null;
    } catch {
      return null;
    }
  },
}));

/** 셀렉터 — 리렌더 최소화용. */
export const selectIsAuthenticated = (s: AuthState) => s.level !== 'guest';
/** 워크스페이스 회원(사용자 유형이 있음). */
export const selectIsWorkspaceMember = (s: AuthState) => s.axis !== null;
/** 기능 사용 가능 여부 — 서버 기능 허용 표의 결과(세션 features)로만 판단한다. */
export const selectHasFeature = (feature: WorkspaceFeature) => (s: AuthState) =>
  s.features.includes(feature);
